import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calculator,
  BarChart3,
  Eye,
  Edit,
  Trash2,
  Plus,
  Save,
  RefreshCw,
  Download,
  AlertTriangle,
  Calendar,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  X,
  Activity,
  HelpCircle,
  MoreHorizontal,
} from 'lucide-react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart as RechartsLineChart,
  Line,
} from 'recharts';
import { toPng } from 'html-to-image';
import { pdf } from '@react-pdf/renderer';
import FinancialReportPDF from './FinancialReportPDF';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import FinancialValuationService from '../../services/financialValuationService';
import { assetService } from '../../services/assetService';
import MasterDataService from '../../services/masterDataService';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import {
  generateDepreciationSchedule,
  calculateImpairment,
} from '../../utils/depreciationCalculations';

const ActionDropdown = ({ asset, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 220);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                onClick={() => {
                  setIsOpen(false);
                  onView(asset);
                }}
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View
              </button>
            </li>

            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                onClick={() => {
                  setIsOpen(false);
                  onEdit(asset);
                }}
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                onClick={() => {
                  setIsOpen(false);
                  onDelete(asset);
                }}
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const FinancialValuation = () => {
  const { toasts, success, error: showError, removeToast } = useToast();
  const chartRefs = {
    category: useRef(null),
    depreciation: useRef(null),
    methods: useRef(null),
    impairment: useRef(null),
    summary: useRef(null),
  };

  // Add CSS for animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromRight {
        from {
          transform: translateX(100%);
        }
        to {
          transform: translateX(0);
        }
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .animate-spin {
        animation: spin 1s linear infinite;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [activeTab, setActiveTab] = useState('valuation');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImpairmentModal, setShowImpairmentModal] = useState(false);
  const [showDepreciationModal, setShowDepreciationModal] = useState(false);
  const [showImpairmentDeleteModal, setShowImpairmentDeleteModal] = useState(false);
  const [impairmentToDelete, setImpairmentToDelete] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [allAssets, setAllAssets] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepreciation, setFilterDepreciation] = useState('all');
  const [depreciationSchedule, setDepreciationSchedule] = useState([]);
  const [impairmentTests, setImpairmentTests] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [reportsData, setReportsData] = useState({
    summary: null,
    depreciation: null,
    impairment: null,
    category: null,
  });

  // Financial data state - moved up to avoid initialization error
  const [financialData, setFinancialData] = useState([]);

  // Dynamically generated list of unique asset categories
  const [availableCategories, setAvailableCategories] = useState([]);

  // Load data on component mount
  useEffect(() => {
    loadFinancialData();
    loadAvailableAssets();
    loadAllAssets();
  }, []);

  // Populate form data when edit modal opens
  useEffect(() => {
    if (showEditModal && selectedAsset) {
      // Helper to convert ID to string (handles both ObjectId and string)
      const getIdAsString = (id) => {
        if (!id) return '';
        if (typeof id === 'string') return id;
        if (typeof id === 'object' && id !== null) {
          // If it's a populated object, extract _id
          if (id._id) return getIdAsString(id._id);
          // If it has toString method, use it
          if (id.toString) return id.toString();
        }
        if (id.toString) return id.toString();
        return String(id);
      };

      // Format dates for HTML date inputs (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch (error) {
          return '';
        }
      };

      // Extract assetId from financial valuation record
      // assetId could be: ObjectId, string, or populated object { _id: ..., name: ... }
      let assetIdFromValuation = '';
      if (selectedAsset.assetId) {
        if (
          typeof selectedAsset.assetId === 'object' &&
          selectedAsset.assetId !== null
        ) {
          // Populated object - extract _id
          assetIdFromValuation = getIdAsString(
            selectedAsset.assetId._id || selectedAsset.assetId.id,
          );
        } else {
          // String or ObjectId
          assetIdFromValuation = getIdAsString(selectedAsset.assetId);
        }
      }

      // Also check assetIdString (stored as string in the record)
      const assetIdString = selectedAsset.assetIdString || '';

      // Find matching asset in availableAssets
      // Try matching by _id first, then by assetCode/assetId
      const matchingAsset = availableAssets.find((a) => {
        // Normalize IDs to strings for comparison
        const aId = String(a._id || a.id || '');
        const aCode = String(a.assetCode || a.assetId || '');
        const normalizedAssetIdFromValuation = String(
          assetIdFromValuation || '',
        );
        const normalizedAssetIdString = String(assetIdString || '');

        // Match by _id (exact string comparison)
        if (
          aId &&
          normalizedAssetIdFromValuation &&
          aId === normalizedAssetIdFromValuation
        ) {
          return true;
        }

        // Match by assetCode/assetId string
        if (
          aCode &&
          normalizedAssetIdString &&
          aCode === normalizedAssetIdString
        ) {
          return true;
        }

        // Also try matching assetCode with the ObjectId string (in case assetCode was stored as ObjectId)
        if (
          aCode &&
          normalizedAssetIdFromValuation &&
          aCode === normalizedAssetIdFromValuation
        ) {
          return true;
        }

        return false;
      });

      // Use the matching asset's _id for the dropdown
      let finalAssetId = '';
      if (matchingAsset) {
        finalAssetId = String(matchingAsset._id || matchingAsset.id || '');
      } else if (assetIdFromValuation) {
        // If no match found but we have an assetId, use it anyway
        finalAssetId = String(assetIdFromValuation);
      }

      // Debug logging (can be removed later)
      if (showEditModal) {
        console.log('Edit Modal - Asset Matching:', {
          assetIdFromValuation: String(assetIdFromValuation || ''),
          assetIdString: String(assetIdString || ''),
          availableAssetsCount: availableAssets.length,
          matchingAsset: matchingAsset
            ? {
              _id: String(matchingAsset._id || ''),
              id: String(matchingAsset.id || ''),
              assetCode: String(matchingAsset.assetCode || ''),
            }
            : null,
          finalAssetId: String(finalAssetId || ''),
          availableAssetsSample: availableAssets.slice(0, 5).map((a) => ({
            _id: String(a._id || ''),
            id: String(a.id || ''),
            assetCode: String(a.assetCode || ''),
            name: a.name,
          })),
          comparisonDetails: availableAssets.slice(0, 3).map((a) => ({
            assetId: String(a._id || a.id || ''),
            matchesById:
              String(a._id || a.id || '') ===
              String(assetIdFromValuation || ''),
            assetCode: String(a.assetCode || ''),
            matchesByCode:
              String(a.assetCode || '') === String(assetIdString || ''),
          })),
        });
      }

      setFormData({
        assetId: finalAssetId,
        assetName: selectedAsset.assetName || '',
        category: selectedAsset.category || '',
        subcategory: selectedAsset.subcategory || '',
        purchaseDate: formatDateForInput(selectedAsset.purchaseDate),
        purchasePrice: selectedAsset.purchasePrice || 0,
        currentValue: selectedAsset.currentValue || 0,
        depreciationMethod: selectedAsset.depreciationMethod || 'Straight Line',
        depreciationRate: selectedAsset.depreciationRate || 20,
        usefulLife: selectedAsset.usefulLife || 5,
        currency: selectedAsset.currency || 'USD',
        vendor: selectedAsset.vendor || '',
        warrantyExpiry: selectedAsset.warrantyExpiry
          ? formatDateForInput(selectedAsset.warrantyExpiry)
          : '',
        location: selectedAsset.location || '',
        department: selectedAsset.department || '',
        assignedTo: selectedAsset.assignedTo || '',
        status: selectedAsset.status || 'Active',
        valuationMethod: selectedAsset.valuationMethod || 'Cost Model',
        notes: selectedAsset.notes || '',
      });
    } else if (!showEditModal && !showAddModal) {
      // Reset form when modal closes
      setFormData({
        assetId: '',
        assetName: '',
        category: '',
        subcategory: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        purchasePrice: 0,
        currentValue: 0,
        depreciationMethod: 'Straight Line',
        depreciationRate: 20,
        usefulLife: 5,
        currency: 'USD',
        vendor: '',
        warrantyExpiry: '',
        location: '',
        department: '',
        assignedTo: '',
        status: 'Active',
        valuationMethod: 'Cost Model',
        notes: '',
      });
    }
  }, [showEditModal, selectedAsset, availableAssets]);

  // Load depreciation schedules when switching to depreciation tab
  useEffect(() => {
    if (activeTab === 'depreciation' && financialData.length > 0) {
      loadDepreciationSchedules();
    }
  }, [activeTab, financialData]);

  // Load impairment tests when switching to impairment tab
  useEffect(() => {
    if (activeTab === 'impairment' && financialData.length > 0) {
      loadImpairmentTests();
    }
  }, [activeTab, financialData]);

  // Load reports data when switching to reports tab
  useEffect(() => {
    if (activeTab === 'reports' && financialData.length > 0) {
      loadReportsData();
    }
  }, [activeTab, financialData]);

  // Auto-load depreciation schedule for the first asset when data is available
  useEffect(() => {
    if (financialData.length > 0 && depreciationSchedule.length === 0) {
      const firstAsset = financialData[0];
      if (firstAsset) {
        const params = {
          cost: firstAsset.purchasePrice,
          salvageValue: firstAsset.salvageValue || 0,
          usefulLife: firstAsset.usefulLife || 5,
          rate: (firstAsset.depreciationRate || 20) / 100,
        };
        const sampleSchedule = generateDepreciationSchedule(
          firstAsset.depreciationMethod || 'Straight Line',
          params,
        );
        setDepreciationSchedule(sampleSchedule);
      }
    }
  }, [financialData]);

  // Check for missing financial valuations after initial data loads
  /*
  const hasCheckedValuations = useRef(false);
  useEffect(() => {
    if (financialData.length === 0 && availableAssets.length > 0 && !hasCheckedValuations.current) {
      hasCheckedValuations.current = true;
      createMissingFinancialValuations();
    }
  }, [financialData.length, availableAssets.length]);
  */

  // Load financial valuation data
  const loadFinancialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await FinancialValuationService.getFinancialValuations({
        page: 1,
        limit: 100, // Maximum allowed by validation
      });

      if (response.success) {
        // The API returns { success: true, data: { valuations: [...], pagination: {...} } }
        const valuations =
          response.data?.data?.valuations || response.data?.valuations || [];
        setFinancialData(valuations);
      } else {
        throw new Error(response.message || 'Failed to load financial data');
      }
    } catch (error) {
      console.error('Error loading financial data:', error);
      console.error('Error details:', error.response?.data || error.message);
      showError(
        error.response?.data?.message ||
        error.message ||
        'Failed to load financial valuation data',
      );
      setFinancialData([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Check for assets without financial valuations and create them
  const createMissingFinancialValuations = useCallback(async () => {
    // ABORT: Prevent infinite loop re-creations when an asset is intentionally deleted
    return;
    try {
      // Get all assets
      const assetsResponse =
        await FinancialValuationService.getAssetsForValuation();
      if (!assetsResponse.success) {
        console.error('Failed to load assets:', assetsResponse.message);
        return;
      }

      const assets = assetsResponse.data.assets || [];

      // Get existing financial valuations
      const valuationsResponse =
        await FinancialValuationService.getFinancialValuations({
          page: 1,
          limit: 100,
        });

      const existingValuations = valuationsResponse.success
        ? valuationsResponse.data.valuations || []
        : [];

      // Find assets without financial valuations
      const assetsWithoutValuations = assets.filter(
        (asset) =>
          !existingValuations.some(
            (valuation) =>
              valuation.assetId === asset._id ||
              valuation.assetIdString === asset.assetId,
          ),
      );

      if (assetsWithoutValuations.length > 0) {
        // Create financial valuations for assets that don't have them
        for (const asset of assetsWithoutValuations) {
          const valuationData = {
            assetId: asset._id,
            assetName: asset.name,
            assetIdString: asset.assetId,
            category: asset.type || 'Other',
            subcategory: asset.subtype || '',
            purchaseDate:
              asset.purchaseDate || new Date().toISOString().split('T')[0],
            purchasePrice: asset.cost || asset.currentValue || 0,
            currentValue: asset.currentValue || asset.cost || 0,
            depreciationMethod: asset.depreciationMethod || 'Straight Line',
            depreciationRate: 20, // Default 20%
            usefulLife: 5, // Default 5 years
            currency: 'USD',
            vendor:
              typeof asset.vendor === 'object'
                ? asset.vendor?.name || ''
                : asset.vendor || '',
            warrantyExpiry: asset.warrantyExpiry || null,
            location:
              typeof asset.location === 'object'
                ? asset.location?.building || asset.location?.address || ''
                : asset.location || '',
            department: asset.department || '',
            assignedTo: asset.assignedTo || '',
            status: asset.status || 'Active',
            valuationMethod: 'Cost Model',
            notes: 'Auto-created from existing asset data',
          };

          try {
            await FinancialValuationService.createFinancialValuation(
              valuationData,
            );
          } catch (error) {
            console.error(
              `Failed to create financial valuation for asset ${asset.name}:`,
              error,
            );
          }
        }

        // Reload financial data after creating missing valuations
        await loadFinancialData();
        success(
          `Created financial valuations for ${assetsWithoutValuations.length} assets`,
        );
      }
    } catch (error) {
      console.error('Error creating missing financial valuations:', error);
      showError('Failed to create missing financial valuations');
    }
  }, [success, showError, loadFinancialData]);

  // (loadFinancialData moved up)

  // Load available assets for dropdown
  const loadAvailableAssets = useCallback(async () => {
    try {
      setLoadingAssets(true);
      const response = await FinancialValuationService.getAssetsForValuation();

      if (response.success) {
        const assets = response.data.assets || response.data || [];

        // Normalize assets to ensure _id is always a string for proper matching
        const normalizedAssets = assets.map((asset) => {
          const assetId = asset._id?.toString() || asset.id?.toString() || '';
          const assetCode = asset.assetCode || asset.assetId || '';

          return {
            ...asset,
            _id: assetId,
            id: assetId,
            assetCode: assetCode,
            assetId: asset.assetId || assetCode,
            name: asset.name || 'Unnamed Asset',
          };
        });

        try {
          // Fetch categories from the Master Data / Categorization module directly
          const assetTypesResponse = await MasterDataService.getByCategory('AssetType');
          const types = assetTypesResponse?.data || assetTypesResponse || [];
          const customCategories = (Array.isArray(types) ? types : []).map(t => t.label).filter(Boolean);

          // Also extract unique categories dynamically based on the available assets map
          const assetCategories = [...new Set(
            normalizedAssets.map(a => a.category || a.type)
          )];

          setAvailableCategories(prev => {
            const merged = [...new Set([...prev, ...customCategories, ...assetCategories])];
            return merged.filter(Boolean).sort();
          });
        } catch (catError) {
          console.error("Failed to fetch categorized layout tree", catError);
          // Fallback to extraction if service fails
          const assetCategories = [...new Set(
            normalizedAssets.map(a => a.category || a.type)
          )];
          setAvailableCategories(prev => [...new Set([...prev, ...assetCategories])].filter(Boolean).sort());
        }

        setAvailableAssets(normalizedAssets);
      } else {
        throw new Error(response.message || 'Failed to load assets');
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      console.error('Error details:', error.response?.data || error.message);
      showError(
        error.response?.data?.message ||
        error.message ||
        'Failed to load available assets',
      );
      setAvailableAssets([]); // Set empty array on error instead of mock data
    } finally {
      setLoadingAssets(false);
    }
  }, [showError]);

  // Load depreciation schedules for all assets
  const loadDepreciationSchedules = useCallback(async () => {
    try {
      setIsLoading(true);
      const allSchedules = [];

      for (const asset of financialData) {
        try {
          const schedule =
            await FinancialValuationService.getDepreciationSchedule(
              asset.assetId || asset._id,
            );
          if (schedule && schedule.schedule) {
            allSchedules.push({
              assetId: asset.assetId || asset._id,
              assetName: asset.assetName,
              schedule: schedule.schedule,
            });
          }
        } catch (error) {
          console.warn(
            `Failed to load schedule for asset ${asset.assetId}:`,
            error,
          );
        }
      }

      // For now, use the first asset's schedule or generate a sample
      if (allSchedules.length > 0) {
        setDepreciationSchedule(allSchedules[0].schedule);
      } else if (financialData.length > 0) {
        // Generate a sample schedule for the first asset
        const firstAsset = financialData[0];
        const params = {
          cost: firstAsset.purchasePrice,
          salvageValue: firstAsset.salvageValue || 0,
          usefulLife: firstAsset.usefulLife || 5,
          rate: (firstAsset.depreciationRate || 20) / 100,
        };
        const sampleSchedule = generateDepreciationSchedule(
          firstAsset.depreciationMethod || 'Straight Line',
          params,
        );
        setDepreciationSchedule(sampleSchedule);
      }
    } catch (error) {
      console.error('Error loading depreciation schedules:', error);
      showError('Failed to load depreciation schedules');
    } finally {
      setIsLoading(false);
    }
  }, [financialData, showError]);

  // Load impairment tests for all assets
  const loadImpairmentTests = useCallback(async () => {
    try {
      setIsLoading(true);
      const allTests = [];

      // Get assets with impairment data
      const impairedAssets = financialData.filter(
        (asset) => asset.impairmentLoss > 0,
      );

      for (const asset of impairedAssets) {
        const testResult = {
          id: `${asset.assetId || asset._id}-${Date.now()}`,
          assetId: asset.assetId || asset._id,
          assetName: asset.assetName,
          testDate:
            asset.lastImpairmentTest || new Date().toISOString().split('T')[0],
          carryingAmount: asset.bookValue || asset.currentValue,
          recoverableAmount: asset.currentValue - asset.impairmentLoss,
          impairmentLoss: asset.impairmentLoss,
          newCarryingAmount: asset.currentValue,
          isImpaired: asset.impairmentLoss > 0,
          testReason: asset.impairmentTestReason || 'Historical impairment',
          notes: asset.impairmentTestNotes || '',
          status: asset.impairmentLoss > 0 ? 'Impaired' : 'No Impairment',
        };
        allTests.push(testResult);
      }

      setImpairmentTests(allTests);
    } catch (error) {
      console.error('Error loading impairment tests:', error);
      showError('Failed to load impairment tests');
    } finally {
      setIsLoading(false);
    }
  }, [financialData, showError]);

  // Load all active assets for dropdown
  const loadAllAssets = async () => {
    try {
      console.log('Loading assets...');
      setIsLoading(true);

      // Try asset service first
      try {
        const response = await assetService.getAssets({
          status: 'Active',
          limit: 100, // API maximum limit is 100
        });

        console.log('Assets response:', response);

        if (response.success) {
          const assets =
            response.data?.data?.assets || response.data?.assets || [];
          console.log('Assets loaded:', assets.length, assets);
          setAllAssets(assets);

          if (assets.length === 0) {
            console.log(
              'No assets found - this might be expected if no assets exist yet',
            );
          }
          return;
        }
      } catch (assetServiceError) {
        console.warn(
          'Asset service failed, trying fallback:',
          assetServiceError,
        );
      }

      // Fallback: Use financial valuation service to get assets
      try {
        const fallbackResponse =
          await FinancialValuationService.getAssetsForValuation();
        console.log('Fallback assets response:', fallbackResponse);

        if (fallbackResponse.success) {
          const assets =
            fallbackResponse.data?.data?.assets ||
            fallbackResponse.data?.assets ||
            [];
          console.log('Fallback assets loaded:', assets.length, assets);
          setAllAssets(assets);
        } else {
          console.error('Both asset services failed');
          showError('Failed to load assets from both services');
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        showError(
          'Failed to load assets: ' +
          (fallbackError.message || 'Network error'),
        );
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      showError('Failed to load assets: ' + (error.message || 'Network error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Load reports data from backend
  const loadReportsData = async () => {
    try {
      setIsLoading(true);

      // Load different types of reports
      const [
        summaryReport,
        depreciationReport,
        impairmentReport,
        categoryReport,
      ] = await Promise.allSettled([
        FinancialValuationService.getFinancialReports('summary'),
        FinancialValuationService.getFinancialReports('depreciation'),
        FinancialValuationService.getFinancialReports('impairment'),
        FinancialValuationService.getFinancialReports('category'),
      ]);

      setReportsData({
        summary:
          summaryReport.status === 'fulfilled' ? summaryReport.value : null,
        depreciation:
          depreciationReport.status === 'fulfilled'
            ? depreciationReport.value
            : null,
        impairment:
          impairmentReport.status === 'fulfilled'
            ? impairmentReport.value
            : null,
        category:
          categoryReport.status === 'fulfilled' ? categoryReport.value : null,
      });
    } catch (error) {
      console.error('Error loading reports data:', error);
      showError('Failed to load reports data');
    } finally {
      setIsLoading(false);
    }
  };

  // Form state for new asset creation
  const [newAssetFormData, setNewAssetFormData] = useState({
    name: '',
    assetId: '',
    type: 'IT Equipment',
    category: 'IT Equipment',
    subcategory: '',
    description: '',
    cost: '',
    currentValue: '',
    purchaseDate: '',
    warrantyExpiry: '',
    supplier: '',
    location: '',
    department: '',
    assignedTo: '',
    status: 'Active',
    specifications: {
      brand: '',
      model: '',
      serialNumber: '',
      specifications: '',
    },
  });

  // Financial information form data
  const [financialFormData, setFinancialFormData] = useState({
    purchaseCost: '',
    currentValue: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    usefulLife: '5',
    depreciationMethod: 'straight-line',
    salvageValue: '0',
    vendor: '',
    warrantyExpiry: '',
    notes: '',
  });

  // Form state for new financial valuation record
  const [formData, setFormData] = useState({
    assetId: '',
    assetName: '',
    category: 'IT Equipment',
    subcategory: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchasePrice: 0,
    currentValue: 0,
    depreciationMethod: 'Straight Line',
    depreciationRate: 20,
    usefulLife: 5,
    currency: 'USD',
    vendor: '',
    warrantyExpiry: '',
    location: '',
    department: '',
    assignedTo: '',
    status: 'Active',
    valuationMethod: 'Cost Model',
    notes: '',
    salvageValue: 0,
    marketValue: 0,
    insuranceValue: 0,
    disposalValue: 0,
  });

  // Impairment test form data
  const [impairmentFormData, setImpairmentFormData] = useState({
    carryingAmount: 0,
    recoverableAmount: 0,
    testDate: new Date().toISOString().split('T')[0],
    testReason: '',
    notes: '',
  });

  // Depreciation calculation form data
  const [depreciationFormData, setDepreciationFormData] = useState({
    method: 'Straight Line',
    rate: 20,
    usefulLife: 5,
    salvageValue: 0,
    totalUnits: 0,
    currentUnits: 0,
    cost: 100000,
    selectedAssetId: null,
  });

  // Calculate dynamic statistics
  const stats = React.useMemo(() => {
    return {
      totalAssets: (financialData || []).length,
      totalValue: (financialData || []).reduce(
        (sum, asset) => sum + (asset?.currentValue || 0),
        0,
      ),
      totalPurchasePrice: (financialData || []).reduce(
        (sum, asset) => sum + (asset?.purchasePrice || 0),
        0,
      ),
      totalDepreciation: (financialData || []).reduce(
        (sum, asset) => sum + (asset?.accumulatedDepreciation || 0),
        0,
      ),
      averageDepreciation:
        (financialData || []).length > 0
          ? (financialData || []).reduce(
            (sum, asset) => sum + (asset?.depreciationRate || 0),
            0,
          ) / (financialData || []).length
          : 0,
      assetsWithImpairment: (financialData || []).filter(
        (asset) => (asset?.impairmentLoss || 0) > 0,
      ).length,
      revaluationSurplus: (financialData || []).reduce(
        (sum, asset) => sum + (asset?.revaluationSurplus || 0),
        0,
      ),
    };
  }, [financialData]);

  // Filtered financial data
  const filteredAssets = (financialData || []).filter((asset) => {
    // Ensure asset is a valid object with required properties
    if (!asset || typeof asset !== 'object') return false;

    // If an asset is selected from dropdown, show only that asset
    if (selectedAssetId) {
      const matchesSelectedAsset =
        asset.assetId === selectedAssetId || asset._id === selectedAssetId;
      if (!matchesSelectedAsset) return false;
    }

    const matchesSearch =
      searchTerm === '' ||
      (asset.assetName &&
        asset.assetName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (asset.assetId &&
        asset.assetId.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (asset.category &&
        asset.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (asset.department &&
        asset.department.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory =
      filterCategory === 'all' || asset.category === filterCategory;
    const matchesStatus =
      filterStatus === 'all' || asset.status === filterStatus;
    const matchesDepreciation =
      filterDepreciation === 'all' ||
      asset.depreciationMethod === filterDepreciation;

    return (
      matchesSearch && matchesCategory && matchesStatus && matchesDepreciation
    );
  });

  const tabs = [
    { id: 'valuation', label: 'Asset Valuation' },
    { id: 'depreciation', label: 'Depreciation' },
    { id: 'impairment', label: 'Impairment' },
    { id: 'reports', label: 'Financial Reports' },
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'valuationId',
      label: 'Valuation ID',
      width: '120px',
      render: (value, row) => row.valuationId || row._id || 'N/A',
    },
    {
      key: 'assetName',
      label: 'Asset',
      width: '250px',
      render: (value, row) => {
        // Handle assetName - could be string or object (if populated)
        let assetName = '';
        if (typeof value === 'string') {
          assetName = value;
        } else if (value && typeof value === 'object') {
          assetName = value.name || value.assetName || '';
        } else if (row.assetName) {
          assetName =
            typeof row.assetName === 'string'
              ? row.assetName
              : row.assetName?.name || row.assetName?.assetName || '';
        }

        // Handle assetId - prefer user-friendly codes, avoid displaying raw Mongo ObjectIds
        let assetId = row.assetId?.assetCode ||
          row.assetId?.assetId ||
          row.assetIdString ||
          '';

        // Strip out the assetId if it's just a 24-character hex MongoDB ObjectId
        if (typeof assetId === 'string' && /^[0-9a-fA-F]{24}$/.test(assetId)) {
          assetId = '';
        }

        return (
          <div>
            <div style={{ fontWeight: '500', color: '#1f2937' }}>
              {assetName || 'Unnamed Asset'}
            </div>
            {assetId && (
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {assetId}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'category',
      label: 'Category',
      width: '120px',
      render: (category) => {
        const categoryStr = String(category || '');

        // Generate a consistent vibrant pastel color based on the category string
        let hash = 0;
        for (let i = 0; i < categoryStr.length; i++) {
          hash = categoryStr.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Define a palette of premium colors (background, text)
        const palettes = [
          { bg: '#dbeafe', color: '#1e40af' }, // Blue
          { bg: '#d1fae5', color: '#065f46' }, // Green
          { bg: '#fef3c7', color: '#92400e' }, // Yellow
          { bg: '#fce7f3', color: '#9d174d' }, // Pink
          { bg: '#e0e7ff', color: '#3730a3' }, // Indigo
          { bg: '#f3e8ff', color: '#6b21a8' }, // Purple
          { bg: '#ffedd5', color: '#9a3412' }  // Orange
        ];

        const colorIndex = Math.abs(hash) % palettes.length;
        const selectedPalette = categoryStr ? palettes[colorIndex] : { bg: '#f3f4f6', color: '#374151' };

        return (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: selectedPalette.bg,
              color: selectedPalette.color,
            }}
          >
            {categoryStr || 'Uncategorized'}
          </span>
        );
      },
    },
    {
      key: 'purchasePrice',
      label: 'Purchase Price',
      width: '130px',
      render: (price, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <DollarSign size={14} style={{ color: '#10b981' }} />
          <span style={{ fontWeight: '500' }}>
            ${(Number(price) || 0).toLocaleString()}
          </span>
        </div>
      ),
    },
    {
      key: 'currentValue',
      label: 'Current Value',
      width: '130px',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <DollarSign size={14} style={{ color: '#007B8F' }} />
          <span style={{ fontWeight: '500' }}>
            ${(Number(value) || 0).toLocaleString()}
          </span>
        </div>
      ),
    },
    {
      key: 'depreciationMethod',
      label: 'Depreciation',
      width: '140px',
      render: (method) => {
        const methodStr = String(method || '');
        return (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor:
                methodStr === 'Straight Line'
                  ? '#d1fae5'
                  : methodStr === 'Declining Balance'
                    ? '#fef3c7'
                    : '#e0e7ff',
              color:
                methodStr === 'Straight Line'
                  ? '#065f46'
                  : methodStr === 'Declining Balance'
                    ? '#92400e'
                    : '#1e40af',
            }}
          >
            {methodStr}
          </span>
        );
      },
    },
    {
      key: 'accumulatedDepreciation',
      label: 'Accum. Depreciation',
      width: '150px',
      render: (depreciation, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingDown size={14} style={{ color: '#ef4444' }} />
          <span style={{ fontWeight: '500' }}>
            ${(Number(depreciation) || 0).toLocaleString()}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      align: 'center',
      render: (_, row) => (
        <ActionDropdown
          asset={row}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  // Handler functions
  const handleView = (asset) => {
    setSelectedAsset(asset);
    setShowViewModal(true);
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    // Reload assets when opening modal to ensure fresh data
    loadAvailableAssets();
    setShowEditModal(true);
    // Form data will be populated by useEffect when showEditModal becomes true
  };

  const handleDelete = (asset) => {
    setSelectedAsset(asset);
    setShowDeleteModal(true);
  };

  const handleExportPDF = async () => {
    setIsLoading(true);
    try {
      // Step A: Capture charts as images
      const images = {};
      for (const [key, ref] of Object.entries(chartRefs)) {
        if (ref.current) {
          // toPng converts the chart container to a high-res image
          images[key] = await toPng(ref.current, {
            quality: 0.95,
            backgroundColor: '#fff',
          });
        }
      }

      // Step B: Generate the PDF Blob using the template
      const doc = <FinancialReportPDF stats={stats} chartImages={images} />;
      const blob = await pdf(doc).toBlob();

      // Step C: Trigger browser download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Financial_Report_${new Date().getTime()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      success('Professional PDF generated successfully!');
    } catch (err) {
      console.error(err);
      showError('Failed to generate PDF');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add new asset
  const handleAddNewAsset = () => {
    // Reset form data and open modal
    setFormData({
      assetId: '',
      assetName: '',
      category: 'IT Equipment',
      subcategory: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchasePrice: 0,
      currentValue: 0,
      depreciationMethod: 'Straight Line',
      depreciationRate: 20,
      usefulLife: 5,
      currency: 'USD',
      vendor: '',
      warrantyExpiry: '',
      location: '',
      department: '',
      assignedTo: '',
      status: 'Active',
      valuationMethod: 'Cost Model',
      notes: '',
    });
    // Reload assets when opening modal to ensure fresh data
    loadAvailableAssets();
    setShowAddModal(true);
  };

  const handleAddNew = () => {
    setFormData({
      assetId: '',
      assetName: '',
      category: 'IT Equipment',
      subcategory: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchasePrice: 0,
      currentValue: 0,
      depreciationMethod: 'Straight Line',
      depreciationRate: 20,
      usefulLife: 5,
      currency: 'USD',
      vendor: '',
      warrantyExpiry: '',
      location: '',
      department: '',
      assignedTo: '',
      status: 'Active',
      valuationMethod: 'Cost Model',
      notes: '',
    });
    setShowAddModal(true);
  };

  // Handle new asset form submission
  const handleNewAssetSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const assetData = {
        ...newAssetFormData,
        cost: parseFloat(newAssetFormData.cost) || 0,
        currentValue:
          parseFloat(newAssetFormData.currentValue) ||
          parseFloat(newAssetFormData.cost) ||
          0,
      };

      const response = await assetService.createAsset(assetData);

      if (response.success) {
        success('Asset created successfully');
        setActiveTab('valuation'); // Switch back to valuation tab

        // Reload assets and financial data
        await loadAllAssets();
        await loadFinancialData();

        // Auto-create financial valuation for the new asset
        try {
          const valuationData = {
            assetId: response.data.asset._id,
            assetName: response.data.asset.name,
            category: response.data.asset.category || 'Other',
            subcategory: response.data.asset.subcategory,
            purchaseDate: response.data.asset.purchaseDate,
            purchasePrice: response.data.asset.cost,
            currentValue: response.data.asset.currentValue,
            depreciationMethod: 'Straight Line',
            depreciationRate: 20,
            usefulLife: 5,
            currency: 'USD',
            vendor: response.data.asset.supplier,
            warrantyExpiry: response.data.asset.warrantyExpiry,
            location: response.data.asset.location,
            department: response.data.asset.department,
            assignedTo: response.data.asset.assignedTo,
            status: 'Active',
            valuationMethod: 'Cost Model',
            notes: `Auto-created for asset: ${response.data.asset.name}`,
          };

          await FinancialValuationService.createFinancialValuation(
            valuationData,
          );
          await loadFinancialData();
          success('Financial valuation created automatically');
        } catch (valuationError) {
          console.error('Error creating financial valuation:', valuationError);
          showError('Asset created but failed to create financial valuation');
        }
      }
    } catch (error) {
      console.error('Error creating asset:', error);
      showError(error.message || 'Failed to create asset');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle financial information submission
  const handleFinancialInfoSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!selectedAssetId) {
        showError('Please select an asset first');
        return;
      }

      const selectedAsset = allAssets.find((a) => a._id === selectedAssetId);
      if (!selectedAsset) {
        showError('Selected asset not found');
        return;
      }

      const financialData = {
        assetId: selectedAssetId,
        assetName: selectedAsset.name,
        category: selectedAsset.category || selectedAsset.type || 'Other',
        subcategory: selectedAsset.category,
        purchaseDate: financialFormData.purchaseDate,
        purchasePrice: parseFloat(financialFormData.purchaseCost) || 0,
        currentValue: parseFloat(financialFormData.currentValue) || 0,
        depreciationMethod: financialFormData.depreciationMethod,
        depreciationRate: 20, // Default rate
        usefulLife: parseInt(financialFormData.usefulLife) || 5,
        currency: 'USD',
        vendor: financialFormData.vendor,
        warrantyExpiry: financialFormData.warrantyExpiry,
        location: selectedAsset.location || '',
        department: selectedAsset.department || '',
        assignedTo: selectedAsset.assignedTo || '',
        status: 'Active',
        valuationMethod: 'Cost Model',
        notes:
          financialFormData.notes ||
          `Financial information added for asset: ${selectedAsset.name}`,
      };

      const response =
        await FinancialValuationService.createFinancialValuation(financialData);

      if (response.success) {
        success('Financial information added successfully');
        setActiveTab('valuation'); // Switch back to valuation tab

        // Reload financial data
        await loadFinancialData();

        // Reset form
        setSelectedAssetId('');
        setFinancialFormData({
          purchaseCost: '',
          currentValue: '',
          purchaseDate: new Date().toISOString().split('T')[0],
          usefulLife: '5',
          depreciationMethod: 'straight-line',
          salvageValue: '0',
          vendor: '',
          warrantyExpiry: '',
          notes: '',
        });
      } else {
        showError(response.message || 'Failed to add financial information');
      }
    } catch (error) {
      console.error('Error adding financial information:', error);
      showError('Failed to add financial information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (showAddModal) {
        // Create new financial valuation record
        const response =
          await FinancialValuationService.createFinancialValuation({
            assetId: formData.assetId,
            assetName: formData.assetName,
            assetIdString: formData.assetId,
            category: formData.category,
            subcategory: formData.subcategory,
            purchaseDate: formData.purchaseDate,
            purchasePrice: parseFloat(formData.purchasePrice),
            currentValue: parseFloat(formData.currentValue),
            depreciationMethod: formData.depreciationMethod,
            depreciationRate: parseFloat(formData.depreciationRate),
            usefulLife: parseInt(formData.usefulLife),
            currency: formData.currency,
            vendor: formData.vendor,
            warrantyExpiry: formData.warrantyExpiry || null,
            location: formData.location,
            department: formData.department,
            assignedTo: formData.assignedTo,
            status: formData.status,
            valuationMethod: formData.valuationMethod,
            notes: formData.notes,
            salvageValue: parseFloat(formData.salvageValue || 0),
            marketValue: parseFloat(
              formData.marketValue || formData.currentValue,
            ),
            insuranceValue: parseFloat(
              formData.insuranceValue || formData.purchasePrice,
            ),
            disposalValue: parseFloat(
              formData.disposalValue || formData.purchasePrice * 0.1,
            ),
          });

        if (response.success) {
          // Reload data to get the latest from server
          await loadFinancialData();
          success('Financial valuation record created successfully');
        } else {
          throw new Error(response.message || 'Failed to create record');
        }
      } else if (showEditModal) {
        // Update existing financial valuation record
        const response =
          await FinancialValuationService.updateFinancialValuation(
            selectedAsset._id,
            {
              assetId: formData.assetId,
              assetName: formData.assetName,
              assetIdString: formData.assetId,
              category: formData.category,
              subcategory: formData.subcategory,
              purchaseDate: formData.purchaseDate,
              purchasePrice: parseFloat(formData.purchasePrice),
              currentValue: parseFloat(formData.currentValue),
              depreciationMethod: formData.depreciationMethod,
              depreciationRate: parseFloat(formData.depreciationRate),
              usefulLife: parseInt(formData.usefulLife),
              currency: formData.currency,
              vendor: formData.vendor,
              warrantyExpiry: formData.warrantyExpiry || null,
              location: formData.location,
              department: formData.department,
              assignedTo: formData.assignedTo,
              status: formData.status,
              valuationMethod: formData.valuationMethod,
              notes: formData.notes,
              salvageValue: parseFloat(formData.salvageValue || 0),
              marketValue: parseFloat(
                formData.marketValue || formData.currentValue,
              ),
              insuranceValue: parseFloat(
                formData.insuranceValue || formData.purchasePrice,
              ),
              disposalValue: parseFloat(
                formData.disposalValue || formData.purchasePrice * 0.1,
              ),
            },
          );

        if (response.success) {
          // Reload data to get the latest from server
          await loadFinancialData();
          success('Financial valuation record updated successfully');
        } else {
          throw new Error(response.message || 'Failed to update record');
        }
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedAsset(null);
    } catch (error) {
      console.error('Error saving financial valuation record:', error);
      showError('Failed to save financial valuation record');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      // Find the ID, using fallback properties to match populated mongo paths
      const idToDelete = selectedAsset.valuationId || selectedAsset._id || selectedAsset.id;

      if (!idToDelete) {
        showError('Could not identify record to delete');
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      const response = await FinancialValuationService.deleteFinancialValuation(
        idToDelete,
      );

      if (response.success) {
        // Reload data to get the latest from server
        await loadFinancialData();
        success('Financial valuation record deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete record');
      }

      setShowDeleteModal(false);
      setSelectedAsset(null);
    } catch (error) {
      console.error('Error deleting financial valuation record:', error);
      showError('Failed to delete financial valuation record');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle asset selection from dropdown
  const handleAssetSelect = (assetId) => {
    const selectedAsset = availableAssets.find(
      (asset) => asset._id === assetId || asset.id === assetId,
    );
    if (selectedAsset) {
      setFormData((prev) => ({
        ...prev,
        assetId: selectedAsset._id || selectedAsset.id,
        assetName: selectedAsset.name,
        category: selectedAsset.category || selectedAsset.type,
      }));
    }
  };

  // Handle depreciation calculation
  const handleDepreciationCalculation = async () => {
    try {
      setIsLoading(true);

      // Get the selected asset from the form
      const selectedAssetId = depreciationFormData.selectedAssetId;
      const selectedAsset = financialData.find(
        (asset) => (asset.assetId || asset._id) === selectedAssetId,
      );

      const params = {
        cost: parseFloat(depreciationFormData.cost),
        salvageValue: parseFloat(depreciationFormData.salvageValue),
        usefulLife: parseInt(depreciationFormData.usefulLife),
        rate: parseFloat(depreciationFormData.rate) / 100,
        totalUnits: parseInt(depreciationFormData.totalUnits),
        currentUnits: parseInt(depreciationFormData.currentUnits),
      };

      if (selectedAsset && selectedAssetId) {
        // Call backend API for depreciation calculation for specific asset
        try {
          const result = await FinancialValuationService.calculateDepreciation(
            selectedAssetId,
            depreciationFormData.method,
            params,
          );

          setDepreciationSchedule(result.schedule || []);
          success(`Depreciation calculated for ${selectedAsset.assetName}`);
        } catch (apiError) {
          console.warn(
            'Backend API failed, using local calculation:',
            apiError,
          );
          // Fallback to local calculation
          const schedule = generateDepreciationSchedule(
            depreciationFormData.method,
            params,
          );
          setDepreciationSchedule(schedule);
          success(
            `Depreciation calculated for ${selectedAsset.assetName} (local calculation)`,
          );
        }
      } else {
        // Calculate general depreciation schedule
        const schedule = generateDepreciationSchedule(
          depreciationFormData.method,
          params,
        );
        setDepreciationSchedule(schedule);
        success('Depreciation schedule generated');
      }
    } catch (error) {
      console.error('Error calculating depreciation:', error);
      showError(
        'Failed to calculate depreciation schedule: ' +
        (error.message || 'Unknown error'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle impairment test
  const handleImpairmentTest = async () => {
    try {
      setIsLoading(true);

      // Get the selected asset from the form or use a default asset
      const selectedAssetId =
        selectedAsset?.assetId ||
        selectedAsset?._id ||
        financialData[0]?.assetId ||
        financialData[0]?._id;
      const selectedAssetName =
        selectedAsset?.assetName ||
        financialData[0]?.assetName ||
        'Unknown Asset';

      const testData = {
        carryingAmount: parseFloat(impairmentFormData.carryingAmount),
        recoverableAmount: parseFloat(impairmentFormData.recoverableAmount),
        testDate: impairmentFormData.testDate,
        testReason: impairmentFormData.testReason,
        notes: impairmentFormData.notes,
      };

      if (selectedAssetId) {
        // Call backend API for impairment test
        try {
          const result = await FinancialValuationService.performImpairmentTest(
            selectedAssetId,
            testData,
          );

          const testResult = {
            id: Date.now(),
            assetId: selectedAssetId,
            assetName: selectedAssetName,
            testDate: testData.testDate,
            carryingAmount: testData.carryingAmount,
            recoverableAmount: testData.recoverableAmount,
            impairmentLoss: result.testResult.impairmentLoss,
            newCarryingAmount: result.testResult.newCarryingAmount,
            isImpaired: result.testResult.isImpaired,
            testReason: testData.testReason,
            notes: testData.notes,
            status: result.testResult.isImpaired ? 'Impaired' : 'No Impairment',
          };

          setImpairmentTests((prev) => [...prev, testResult]);
          setShowImpairmentModal(false);
          success(`Impairment test completed for ${selectedAssetName}`);

          // Refresh financial data to show updated impairment values
          loadFinancialData();
        } catch (apiError) {
          console.warn(
            'Backend API failed, using local calculation:',
            apiError,
          );
          // Fallback to local calculation
          const impairment = calculateImpairment(
            testData.carryingAmount,
            testData.recoverableAmount,
          );

          const testResult = {
            id: Date.now(),
            assetId: selectedAssetId,
            assetName: selectedAssetName,
            testDate: testData.testDate,
            carryingAmount: testData.carryingAmount,
            recoverableAmount: testData.recoverableAmount,
            impairmentLoss: impairment.impairmentLoss,
            newCarryingAmount: impairment.newCarryingAmount,
            isImpaired: impairment.isImpaired,
            testReason: testData.testReason,
            notes: testData.notes,
            status: impairment.isImpaired ? 'Impaired' : 'No Impairment',
          };

          setImpairmentTests((prev) => [...prev, testResult]);
          setShowImpairmentModal(false);
          success(
            `Impairment test completed for ${selectedAssetName} (local calculation)`,
          );
        }
      } else {
        showError('Please select an asset to perform impairment test');
      }
    } catch (error) {
      console.error('Error performing impairment test:', error);
      showError(
        'Failed to perform impairment test: ' +
        (error.message || 'Unknown error'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle impairment test deletion
  // Handle impairment test deletion button click
  const handleDeleteImpairmentTest = (test) => {
    setImpairmentToDelete(test);
    setShowImpairmentDeleteModal(true);
  };

  // Actually wipe the impairment test out of the DB upon user confirmation
  const handleConfirmDeleteImpairmentTest = async () => {
    if (!impairmentToDelete) return;

    try {
      setIsLoading(true);

      // Ensure backend deletes it from the financial valuation array if it exists
      await FinancialValuationService.deleteImpairmentTest(impairmentToDelete.assetId, impairmentToDelete.id)
        .catch(err => console.warn('Backend delete may be unhandled:', err));

      // Pull it out of the active Frontend state table
      setImpairmentTests((prev) => prev.filter((t) => t.id !== impairmentToDelete.id));
      success('Impairment test deleted successfully');

      // Silently reload the asset's financial calculation state
      loadFinancialData();
    } catch (error) {
      console.error('Error deleting impairment test:', error);
      showError('Failed to delete impairment test');
    } finally {
      setIsLoading(false);
      setShowImpairmentDeleteModal(false);
      setImpairmentToDelete(null);
    }
  };



  // Handle export functionality
  const handleExport = async (format = 'excel') => {
    try {
      setIsLoading(true);

      const response = await FinancialValuationService.exportFinancialData(
        format,
        {
          searchTerm,
          category: filterCategory !== 'all' ? filterCategory : undefined,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          depreciationMethod:
            filterDepreciation !== 'all' ? filterDepreciation : undefined,
        },
      );

      if (format === 'excel') {
        // Handle Excel export from API
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `financial-valuations-${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback to CSV export using current filtered data
        const dataToExport = filteredAssets.map((asset) => ({
          'Valuation ID': asset._id || asset.id,
          'Asset ID': asset.assetId,
          'Asset Name': asset.assetName,
          Category: asset.category,
          'Purchase Price': asset.purchasePrice,
          'Current Value': asset.currentValue,
          'Depreciation Method': asset.depreciationMethod,
          'Accumulated Depreciation': asset.accumulatedDepreciation,
          'Book Value': asset.bookValue,
          Status: asset.status,
        }));

        // Create CSV content
        const csvContent = [
          Object.keys(dataToExport[0]).join(','),
          ...dataToExport.map((row) => Object.values(row).join(',')),
        ].join('\n');

        // Download file
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `financial-valuations-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }

      success('Financial data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      showError('Failed to export financial data');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    await loadFinancialData();
    await loadAvailableAssets();
    await loadAllAssets();
    success('Financial data refreshed successfully');
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/assets/financial-valuation';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no',
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={isLoading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh financial data',
      disabled: isLoading,
    },
    {
      label: 'Add Financial Info',
      icon: <Plus size={18} />,
      onClick: handleAddNewAsset,
      variant: 'primary',
      tooltip: 'Add financial information for an asset',
    },
  ];
  const getTrend = (value) => {
    if (value > 0) {
      return {
        icon: <ArrowUpRight size={14} />,
        color: '#059669',
        text: `+${value}%`,
      };
    }
    return {
      icon: <ArrowDownRight size={14} />,
      color: '#DC2626',
      text: `${value}%`,
    };
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Financial & Valuation Management"
        description="Manage asset depreciation, valuation, insurance, and financial reporting"
        actions={headerActions}
      />
      <div
        className="page-container"
        style={{ width: '100%', maxWidth: '100%' }}
      >
        {/* Tabbed Navigation */}
        <div className="project-tabs">
          <div className="project-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">
                  {tab.id === 'valuation' && <Calculator size={18} />}
                  {tab.id === 'depreciation' && <TrendingDown size={18} />}
                  {tab.id === 'impairment' && <AlertTriangle size={18} />}
                  {tab.id === 'reports' && <BarChart3 size={18} />}
                  {tab.id === 'add-asset' && <DollarSign size={18} />}
                </div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Financial KPI Cards – Dashboard-style */}
        <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mt-6 mb-8">
          {/* Total Assets */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div
                className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
                style={{ color: '#059669' }}
              >
                <ArrowUpRight size={14} /> +6.2%
              </div>
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <Package size={20} className="text-[#2D7DD2]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalAssets}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Assets
                </div>
              </div>
            </div>
          </div>
          {/* Total Current Value */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div
                className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
                style={{ color: '#059669' }}
              >
                <ArrowUpRight size={14} /> +9.4%
              </div>

              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} className="text-[#2D7DD2]" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  ${stats.totalValue.toLocaleString()}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Current Value
                </div>
              </div>
            </div>
          </div>

          {/* Total Depreciation */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div
                className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
                style={{ color: '#DC2626' }}
              >
                <ArrowDownRight size={14} /> −12.1%
              </div>

              <div className="flex h-11 w-11 items-center justify-center bg-[#FEF3C7]">
                <TrendingDown size={20} className="text-[#92400E]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  ${stats.totalDepreciation.toLocaleString()}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Depreciation
                </div>
              </div>
            </div>
          </div>

          {/* Purchase Price */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div
                className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
                style={{ color: '#059669' }}
              >
                <ArrowUpRight size={14} /> +4.8%
              </div>

              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <Calculator size={20} className="text-[#2D7DD2]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  ${stats.totalPurchasePrice.toLocaleString()}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Purchase Price
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'valuation' && (
          <div className="project-tab-content">
            <DataTable
              data={filteredAssets || []}
              columns={columns}
              title="Financial Valuation Records"
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={false}
              showCheckboxes={false}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              searchPlaceholder="Search assets by name, ID, or category..."
              emptyMessage="No valuation records found. Click 'Add Financial Info' to get started."
            />
          </div>
        )}

        {activeTab === 'depreciation' && (
          <div className="project-tab-content">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Depreciation Management</h5>
                <button
                  onClick={() => setShowDepreciationModal(true)}
                  className="btn btn-primary"
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    fontSize: '15px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <Calculator size={18} />
                  {isLoading ? 'Calculating...' : 'Calculate Depreciation'}
                </button>
              </div>
              <div className="card-body">
                {depreciationSchedule.length > 0 ? (
                  <>
                    {/* Depreciation KPI Cards – Dashboard style */}
                    <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-8 mt-4">
                      {/* Original Cost */}
                      <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                        <div className="h-1 w-full bg-[var(--primary-color)]" />
                        <div className="relative flex items-center gap-3 p-4">
                          <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                            <DollarSign size={20} className="text-[#2D7DD2]" />
                          </div>
                          <div>
                            <div className="text-[20px] font-bold text-[var(--text-primary)]">
                              $
                              {(
                                depreciationSchedule[0]?.cost ||
                                depreciationFormData.cost ||
                                0
                              ).toLocaleString()}
                            </div>
                            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                              Original Cost
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Final Book Value */}
                      <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                        <div className="h-1 w-full bg-[var(--primary-color)]" />
                        <div className="relative flex items-center gap-3 p-4">
                          <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                            <TrendingUp size={20} className="text-[#2D7DD2]" />
                          </div>
                          <div>
                            <div className="text-[20px] font-bold text-[var(--text-primary)]">
                              $
                              {(
                                depreciationSchedule.at(-1)?.bookValue || 0
                              ).toLocaleString()}
                            </div>
                            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                              Final Book Value
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Total Depreciation */}
                      <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                        <div className="h-1 w-full bg-[var(--primary-color)]" />
                        <div className="relative flex items-center gap-3 p-4">
                          <div className="flex h-11 w-11 items-center justify-center bg-[#FEF3C7]">
                            <TrendingDown
                              size={20}
                              className="text-[#92400E]"
                            />
                          </div>
                          <div>
                            <div className="text-[20px] font-bold text-[var(--text-primary)]">
                              $
                              {(
                                depreciationSchedule.at(-1)
                                  ?.accumulatedDepreciation || 0
                              ).toLocaleString()}
                            </div>
                            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                              Total Depreciation
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Useful Life */}
                      <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                        <div className="h-1 w-full bg-[var(--primary-color)]" />
                        <div className="relative flex items-center gap-3 p-4">
                          <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                            <Calendar size={20} className="text-[#2D7DD2]" />
                          </div>
                          <div>
                            <div className="text-[20px] font-bold text-[var(--text-primary)]">
                              {depreciationSchedule.length}
                            </div>
                            <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                              Useful Life (Years)
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-lg-7 col-md-12 mb-4">
                        <h6>Depreciation Schedule</h6>
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Year</th>
                                <th>Annual Depreciation</th>
                                <th>Accumulated</th>
                                <th>Book Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {depreciationSchedule
                                .slice(0, 10)
                                .map((item, index) => (
                                  <tr key={index}>
                                    <td>
                                      <strong>{item.year}</strong>
                                    </td>
                                    <td>
                                      $
                                      {(
                                        item.annualDepreciation || 0
                                      ).toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                    <td>
                                      $
                                      {(
                                        item.accumulatedDepreciation || 0
                                      ).toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                    <td>
                                      $
                                      {(item.bookValue || 0).toLocaleString(
                                        'en-US',
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          {depreciationSchedule.length > 10 && (
                            <div className="text-center mt-2">
                              <small className="text-muted">
                                Showing first 10 years of{' '}
                                {depreciationSchedule.length} total years
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-5 col-md-12">
                        <h6>Depreciation Chart</h6>
                        <div style={{ height: '350px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsLineChart data={depreciationSchedule}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="year" />
                              <YAxis />
                              <Tooltip
                                formatter={(value, name) => [
                                  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                  name === 'bookValue'
                                    ? 'Book Value'
                                    : name === 'accumulatedDepreciation'
                                      ? 'Accumulated Depreciation'
                                      : name === 'annualDepreciation'
                                        ? 'Annual Depreciation'
                                        : name,
                                ]}
                              />
                              <Line
                                type="monotone"
                                dataKey="bookValue"
                                stroke="#8884d8"
                                strokeWidth={2}
                                name="Book Value"
                              />
                              <Line
                                type="monotone"
                                dataKey="accumulatedDepreciation"
                                stroke="#82ca9d"
                                strokeWidth={2}
                                name="Accumulated Depreciation"
                              />
                            </RechartsLineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <Calculator size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">
                      No Depreciation Schedule Available
                    </h6>
                    <p className="text-muted mb-3">
                      Click "Calculate Depreciation" to generate a depreciation
                      schedule for your assets.
                    </p>
                    <button
                      onClick={() => setShowDepreciationModal(true)}
                      className="btn btn-primary"
                    >
                      <Calculator size={16} className="me-2" />
                      Calculate Depreciation
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'impairment' && (
          <div className="project-tab-content">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Impairment Testing</h5>
                <button
                  onClick={() => setShowImpairmentModal(true)}
                  className="btn btn-primary"
                  style={{
                    padding: '10px 20px',
                    fontSize: '15px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  Perform Impairment Test
                </button>
              </div>
              <div className="card-body">
                {impairmentTests.length > 0 ? (
                  <>
                    {/* Impairment KPI Cards */}
                    <div className="page-stats-grid mb-4">
                      <div className="page-stat-card">
                        <div className="card h-100 page-kpi-card">
                          <div className="card-body page-kpi-body">
                            <div className="page-kpi-content">
                              <div className="page-kpi-text">
                                <h3 className="page-kpi-value color-1">
                                  {impairmentTests.length}
                                </h3>
                                <p className="page-kpi-label">Total Tests</p>
                              </div>
                              <div className="page-kpi-icon-container color-1">
                                <AlertTriangle className="page-kpi-icon color-1" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="page-stat-card">
                        <div className="card h-100 page-kpi-card">
                          <div className="card-body page-kpi-body">
                            <div className="page-kpi-content">
                              <div className="page-kpi-text">
                                <h3 className="page-kpi-value color-2">
                                  {
                                    impairmentTests.filter(
                                      (test) => test.isImpaired,
                                    ).length
                                  }
                                </h3>
                                <p className="page-kpi-label">
                                  Impaired Assets
                                </p>
                              </div>
                              <div className="page-kpi-icon-container color-2">
                                <TrendingDown className="page-kpi-icon color-2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="page-stat-card">
                        <div className="card h-100 page-kpi-card">
                          <div className="card-body page-kpi-body">
                            <div className="page-kpi-content">
                              <div className="page-kpi-text">
                                <h3 className="page-kpi-value color-3">
                                  $
                                  {impairmentTests
                                    .reduce(
                                      (sum, test) => sum + test.impairmentLoss,
                                      0,
                                    )
                                    .toLocaleString()}
                                </h3>
                                <p className="page-kpi-label">
                                  Total Impairment Loss
                                </p>
                              </div>
                              <div className="page-kpi-icon-container color-3">
                                <DollarSign className="page-kpi-icon color-3" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="page-stat-card">
                        <div className="card h-100 page-kpi-card">
                          <div className="card-body page-kpi-body">
                            <div className="page-kpi-content">
                              <div className="page-kpi-text">
                                <h3 className="page-kpi-value color-5">
                                  {impairmentTests.length > 0
                                    ? Math.round(
                                      (impairmentTests.filter(
                                        (test) => test.isImpaired,
                                      ).length /
                                        impairmentTests.length) *
                                      100,
                                    )
                                    : 0}
                                  %
                                </h3>
                                <p className="page-kpi-label">
                                  Impairment Rate
                                </p>
                              </div>
                              <div className="page-kpi-icon-container color-5">
                                <Activity className="page-kpi-icon color-5" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Impairment Tests Table */}
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Asset</th>
                            <th>Test Date</th>
                            <th>Carrying Amount</th>
                            <th>Recoverable Amount</th>
                            <th>Impairment Loss</th>
                            <th>Status</th>
                            <th>Reason</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {impairmentTests.map((test) => (
                            <tr key={test.id}>
                              <td>
                                <div>
                                  <div style={{ fontWeight: '500' }}>
                                    {test.assetName}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#6b7280',
                                    }}
                                  >
                                    ID: {typeof test.assetId === 'object' && test.assetId !== null ? (test.assetId.assetCode || test.assetId._id) : test.assetId}
                                  </div>
                                </div>
                              </td>
                              <td>
                                {new Date(test.testDate).toLocaleDateString()}
                              </td>
                              <td>${test.carryingAmount.toLocaleString()}</td>
                              <td>
                                ${test.recoverableAmount.toLocaleString()}
                              </td>
                              <td>
                                <span
                                  style={{
                                    color:
                                      test.impairmentLoss > 0
                                        ? '#dc3545'
                                        : '#28a745',
                                    fontWeight: '500',
                                  }}
                                >
                                  ${test.impairmentLoss.toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: test.isImpaired
                                      ? '#f8d7da'
                                      : '#d4edda',
                                    color: test.isImpaired
                                      ? '#721c24'
                                      : '#155724',
                                  }}
                                >
                                  {test.isImpaired
                                    ? 'Impaired'
                                    : 'Not Impaired'}
                                </span>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {test.testReason || 'No reason provided'}
                                </small>
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-link text-danger p-0"
                                  onClick={() => handleDeleteImpairmentTest(test)}
                                  title="Delete Test"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <AlertTriangle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                    <h6 style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                      No Impairment Tests
                    </h6>
                    <p style={{ margin: 0, fontSize: '14px', maxWidth: '400px' }}>
                      Click "Perform Impairment Test" in the top right to start evaluating your assets for impairment.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="project-tab-content">
            {/* Reports Header with Export Options */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Financial Reports Dashboard</h5>
                    <div className="btn-group">
                      <button
                        onClick={() => handleExport('excel')}
                        className="btn btn-outline-success btn-sm"
                        disabled={isLoading}
                      >
                        <Download size={16} className="me-2" />
                        Export Excel
                      </button>
                      <button
                        onClick={handleExportPDF}
                        className="btn btn-outline-primary btn-sm"
                        disabled={isLoading}
                      >
                        <Download size={16} className="me-2" />
                        Export PDF
                      </button>
                      <button
                        onClick={loadReportsData}
                        className="btn btn-outline-secondary btn-sm"
                        disabled={isLoading}
                      >
                        <RefreshCw size={16} className="me-2" />
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Asset Value Distribution and Depreciation Trends */}
            <div className="row mb-4">
              <div className="col-lg-6 col-md-12 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5 className="mb-0">
                      Asset Value Distribution by Category
                    </h5>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '350px' }} ref={chartRefs.category}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={(() => {
                              const categoryData = {};
                              financialData.forEach((asset) => {
                                if (!categoryData[asset.category]) {
                                  categoryData[asset.category] = 0;
                                }
                                categoryData[asset.category] +=
                                  asset.currentValue || 0;
                              });
                              return Object.entries(categoryData).map(
                                ([name, value]) => ({ name, value }),
                              );
                            })()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {(() => {
                              const categoryData = {};
                              financialData.forEach((asset) => {
                                if (!categoryData[asset.category]) {
                                  categoryData[asset.category] = 0;
                                }
                                categoryData[asset.category] +=
                                  asset.currentValue || 0;
                              });
                              return Object.entries(categoryData).map(
                                (entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      [
                                        '#0088FE',
                                        '#00C49F',
                                        '#FFBB28',
                                        '#FF8042',
                                        '#8884d8',
                                        '#82ca9d',
                                      ][index % 6]
                                    }
                                  />
                                ),
                              );
                            })()}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [
                              `$${value.toLocaleString()}`,
                              'Value',
                            ]}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 col-md-12 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5 className="mb-0">Asset Value vs Depreciation</h5>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '350px' }} ref={chartRefs.depreciation}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsBarChart
                          data={financialData.slice(0, 8).map((asset) => ({
                            name:
                              asset.assetName.length > 12
                                ? asset.assetName.substring(0, 12) + '...'
                                : asset.assetName,
                            purchase: asset.purchasePrice || 0,
                            current: asset.currentValue || 0,
                            depreciation: asset.accumulatedDepreciation || 0,
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis />
                          <Tooltip
                            formatter={(value) => [
                              `$${value.toLocaleString()}`,
                              'Value',
                            ]}
                          />
                          <Bar
                            dataKey="purchase"
                            fill="#8884d8"
                            name="Purchase Price"
                          />
                          <Bar
                            dataKey="current"
                            fill="#82ca9d"
                            name="Current Value"
                          />
                          <Bar
                            dataKey="depreciation"
                            fill="#ffc658"
                            name="Accumulated Depreciation"
                          />
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Depreciation and Impairment Analysis */}
            <div className="row mb-4">
              <div className="col-lg-6 col-md-12 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5 className="mb-0">Depreciation Methods Distribution</h5>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '300px' }} ref={chartRefs.methods}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsBarChart
                          data={(() => {
                            const methodData = {};
                            financialData.forEach((asset) => {
                              const method =
                                asset.depreciationMethod || 'Unknown';
                              if (!methodData[method]) {
                                methodData[method] = {
                                  count: 0,
                                  totalValue: 0,
                                };
                              }
                              methodData[method].count += 1;
                              methodData[method].totalValue +=
                                asset.currentValue || 0;
                            });
                            return Object.entries(methodData).map(
                              ([method, data]) => ({
                                method,
                                count: data.count,
                                value: data.totalValue,
                              }),
                            );
                          })()}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="method" />
                          <YAxis />
                          <Tooltip
                            formatter={(value, name) => [
                              name === 'count'
                                ? value
                                : `$${value.toLocaleString()}`,
                              name === 'count' ? 'Count' : 'Total Value',
                            ]}
                          />
                          <Bar
                            dataKey="count"
                            fill="#8884d8"
                            name="Asset Count"
                          />
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 col-md-12 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5 className="mb-0">Impairment Analysis</h5>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '300px' }} ref={chartRefs.impairment}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsBarChart
                          data={(() => {
                            const impairedAssets = financialData.filter(
                              (asset) => asset.impairmentLoss > 0,
                            );
                            const nonImpairedAssets = financialData.filter(
                              (asset) => asset.impairmentLoss === 0,
                            );

                            return [
                              {
                                category: 'Impaired Assets',
                                count: impairedAssets.length,
                                value: impairedAssets.reduce(
                                  (sum, asset) =>
                                    sum + (asset.impairmentLoss || 0),
                                  0,
                                ),
                              },
                              {
                                category: 'Non-Impaired Assets',
                                count: nonImpairedAssets.length,
                                value: 0,
                              },
                            ];
                          })()}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="category" />
                          <YAxis />
                          <Tooltip
                            formatter={(value, name) => [
                              name === 'count'
                                ? value
                                : `$${value.toLocaleString()}`,
                              name === 'count' ? 'Count' : 'Impairment Loss',
                            ]}
                          />
                          <Bar
                            dataKey="count"
                            fill="#ff6b6b"
                            name="Asset Count"
                          />
                          <Bar
                            dataKey="value"
                            fill="#ffa726"
                            name="Impairment Loss"
                          />
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary Report */}
            <div ref={chartRefs.summary}>
              <div className="card" style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{
                  padding: '18px 24px',
                  borderBottom: '1px solid #f0f0f0',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BarChart3 size={18} color="#fff" />
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>Comprehensive Financial Summary</h5>
                    <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>Key performance indicators at a glance</p>
                  </div>
                </div>

                <div style={{ padding: '24px' }}>
                  {/* Primary KPIs - 4 column grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '20px'
                  }}>
                    {/* Total Purchase Price */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '18px 16px', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #eff6ff 0%, #f0f7ff 100%)',
                      border: '1px solid #dbeafe',
                      borderLeft: '4px solid #3b82f6',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                    }}>
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <DollarSign size={22} color="#fff" />
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: '#1e40af', lineHeight: 1.1 }}>
                          ${stats.totalPurchasePrice.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', marginTop: '2px' }}>Total Purchase Price</div>
                        <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '500', marginTop: '1px' }}>Initial Investment</div>
                      </div>
                    </div>

                    {/* Total Current Value */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '18px 16px', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                      border: '1px solid #bbf7d0',
                      borderLeft: '4px solid #10b981',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                    }}>
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <TrendingUp size={22} color="#fff" />
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: '#065f46', lineHeight: 1.1 }}>
                          ${stats.totalValue.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', marginTop: '2px' }}>Total Current Value</div>
                        <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '500', marginTop: '1px' }}>Current Worth</div>
                      </div>
                    </div>

                    {/* Total Depreciation */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '18px 16px', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #fffbeb 0%, #fef9c3 100%)',
                      border: '1px solid #fde68a',
                      borderLeft: '4px solid #f59e0b',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                    }}>
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <TrendingDown size={22} color="#fff" />
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: '#92400e', lineHeight: 1.1 }}>
                          ${stats.totalDepreciation.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', marginTop: '2px' }}>Total Depreciation</div>
                        <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '500', marginTop: '1px' }}>Accumulated Loss</div>
                      </div>
                    </div>

                    {/* Total Assets */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '18px 16px', borderRadius: '10px',
                      background: 'linear-gradient(135deg, #f0f0ff 0%, #ede9fe 100%)',
                      border: '1px solid #ddd6fe',
                      borderLeft: '4px solid #7c3aed',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                    }}>
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Package size={22} color="#fff" />
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: '#5b21b6', lineHeight: 1.1 }}>
                          {stats.totalAssets}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', marginTop: '2px' }}>Total Assets</div>
                        <div style={{ fontSize: '11px', color: '#7c3aed', fontWeight: '500', marginTop: '1px' }}>Asset Count</div>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{ borderTop: '1px dashed #e5e7eb', margin: '8px 0 20px' }} />

                  {/* Secondary KPIs - 3 column grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '16px'
                  }}>
                    {/* Depreciation Rate */}
                    <div style={{
                      padding: '20px', borderRadius: '10px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      textAlign: 'center',
                      position: 'relative', overflow: 'hidden',
                    }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)' }} />
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#4f46e5', lineHeight: 1 }}>
                        {stats.totalAssets > 0 && stats.totalPurchasePrice > 0
                          ? Math.round((stats.totalDepreciation / stats.totalPurchasePrice) * 100)
                          : 0}%
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '6px' }}>Depreciation Rate</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>Overall portfolio depreciation</div>
                    </div>

                    {/* Value Retention */}
                    <div style={{
                      padding: '20px', borderRadius: '10px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      textAlign: 'center',
                      position: 'relative', overflow: 'hidden',
                    }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #0ea5e9, #06b6d4)' }} />
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#0284c7', lineHeight: 1 }}>
                        {stats.totalAssets > 0 && stats.totalPurchasePrice > 0
                          ? Math.round((stats.totalValue / stats.totalPurchasePrice) * 100)
                          : 0}%
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '6px' }}>Value Retention</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>Current vs purchase price</div>
                    </div>

                    {/* Avg Depreciation per Asset */}
                    <div style={{
                      padding: '20px', borderRadius: '10px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      textAlign: 'center',
                      position: 'relative', overflow: 'hidden',
                    }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #f43f5e, #fb7185)' }} />
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#be123c', lineHeight: 1 }}>
                        ${stats.totalAssets > 0
                          ? Math.round((stats.totalPurchasePrice - stats.totalValue) / stats.totalAssets).toLocaleString()
                          : 0}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '6px' }}>Avg. Depreciation per Asset</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>Per asset value loss</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Financial Info Tab - Removed, now using modal */}
        {false && activeTab === 'add-asset' && (
          <div className="project-tab-content">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">+ Add Financial Information</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleFinancialInfoSubmit}>
                  {/* Row 1: Asset Selection */}
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '15px',
                      marginBottom: '20px',
                    }}
                  >
                    <h6 className="text-primary mb-3" style={{ width: '100%' }}>
                      Select Asset
                    </h6>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <label className="form-label">Select Asset *</label>
                      <select
                        className="form-control"
                        value={selectedAssetId}
                        onChange={(e) => setSelectedAssetId(e.target.value)}
                        required
                      >
                        <option value="">Choose an asset...</option>
                        {allAssets.map((asset) => (
                          <option key={asset._id} value={asset._id}>
                            {asset.assetId} - {asset.name} ({asset.type})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Asset Details (Read-only) */}
                  {selectedAssetId && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '15px',
                        marginBottom: '20px',
                      }}
                    >
                      <h6
                        className="text-primary mb-3"
                        style={{ width: '100%' }}
                      >
                        Asset Details (Read-only)
                      </h6>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Asset Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            allAssets.find((a) => a._id === selectedAssetId)
                              ?.assetId || ''
                          }
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Asset Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            allAssets.find((a) => a._id === selectedAssetId)
                              ?.name || ''
                          }
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Asset Type</label>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            allAssets.find((a) => a._id === selectedAssetId)
                              ?.type || ''
                          }
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Department</label>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            allAssets.find((a) => a._id === selectedAssetId)
                              ?.department || ''
                          }
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Row 3: Financial Information */}
                  {selectedAssetId && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '15px',
                        marginBottom: '20px',
                      }}
                    >
                      <h6
                        className="text-primary mb-3"
                        style={{ width: '100%' }}
                      >
                        Financial Information
                      </h6>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Purchase Cost *</label>
                        <input
                          type="number"
                          className="form-control"
                          value={financialFormData.purchaseCost}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              purchaseCost: e.target.value,
                            }))
                          }
                          required
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">
                          Current Market Value *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={financialFormData.currentValue}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              currentValue: e.target.value,
                            }))
                          }
                          required
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Purchase Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={financialFormData.purchaseDate}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              purchaseDate: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">
                          Useful Life (Years) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={financialFormData.usefulLife}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              usefulLife: e.target.value,
                            }))
                          }
                          required
                          min="1"
                          placeholder="5"
                        />
                      </div>
                    </div>
                  )}

                  {/* Row 4: Additional Financial Details */}
                  {selectedAssetId && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '15px',
                        marginBottom: '20px',
                      }}
                    >
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">
                          Depreciation Method
                        </label>
                        <select
                          className="form-control"
                          value={financialFormData.depreciationMethod}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              depreciationMethod: e.target.value,
                            }))
                          }
                        >
                          <option value="straight-line">Straight Line</option>
                          <option value="declining-balance">
                            Declining Balance
                          </option>
                          <option value="sum-of-years">Sum of Years</option>
                          <option value="units-of-production">
                            Units of Production
                          </option>
                        </select>
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Salvage Value</label>
                        <input
                          type="number"
                          className="form-control"
                          value={financialFormData.salvageValue}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              salvageValue: e.target.value,
                            }))
                          }
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Vendor/Supplier</label>
                        <input
                          type="text"
                          className="form-control"
                          value={financialFormData.vendor}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              vendor: e.target.value,
                            }))
                          }
                          placeholder="Enter vendor name"
                        />
                      </div>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <label className="form-label">Warranty Expiry</label>
                        <input
                          type="date"
                          className="form-control"
                          value={financialFormData.warrantyExpiry}
                          onChange={(e) =>
                            setFinancialFormData((prev) => ({
                              ...prev,
                              warrantyExpiry: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}

                  {/* Row 5: Notes */}
                  {selectedAssetId && (
                    <div style={{ marginBottom: '20px' }}>
                      <label className="form-label">Financial Notes</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={financialFormData.notes}
                        onChange={(e) =>
                          setFinancialFormData((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        placeholder="Additional financial information or notes..."
                      />
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="d-flex gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setSelectedAssetId('');
                            setFinancialFormData({
                              purchaseCost: '',
                              currentValue: '',
                              purchaseDate: new Date()
                                .toISOString()
                                .split('T')[0],
                              usefulLife: '5',
                              depreciationMethod: 'straight-line',
                              salvageValue: '0',
                              vendor: '',
                              warrantyExpiry: '',
                              notes: '',
                            });
                          }}
                        >
                          <RefreshCw size={16} className="me-2" />
                          Reset
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isLoading || !selectedAssetId}
                        >
                          {isLoading ? (
                            <>
                              <RefreshCw
                                size={16}
                                className="me-2"
                                style={{ animation: 'spin 1s linear infinite' }}
                              />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={16} className="me-2" />
                              Add Financial Info
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Financial Valuation Record"
          message={`Are you sure you want to delete financial valuation record ${selectedAsset?.id}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        {/* Add/Edit Modal */}
        {/* Add/Edit Financial Valuation Modal - Full Viewport Side Panel */}
        {(showAddModal || showEditModal) && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease-in-out',
            }}
          >
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '100%',
                maxWidth: '700px',
                height: '100vh',
                backgroundColor: 'white',
                boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideInFromRight 0.3s ease-out',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                }}
              >
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'white',
                    margin: 0,
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {showAddModal
                    ? 'Add New Financial Valuation'
                    : 'Edit Financial Valuation'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedAsset(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'white',
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <form
                onSubmit={handleFormSubmit}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '24px',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px',
                    marginBottom: '24px',
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Asset *
                    </label>
                    <select
                      name="assetId"
                      value={formData.assetId}
                      onChange={(e) => {
                        handleAssetSelect(e.target.value);
                        handleFormChange(e);
                      }}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      <option value="">Select Asset</option>
                      {availableAssets.map((asset) => {
                        // Ensure the value is always a string for proper matching
                        const assetValue = String(asset._id || asset.id || '');
                        return (
                          <option key={assetValue} value={assetValue}>
                            {asset.assetCode || asset.assetId || asset.id} -{' '}
                            {asset.name || 'Unnamed Asset'}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      <option value="">Select Category</option>
                      {availableCategories.map((cat, idx) => (
                        <option key={`cat-${idx}`} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Purchase Price *
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRight: 'none',
                          backgroundColor: '#f9fafb',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)',
                        }}
                      >
                        $
                      </span>
                      <input
                        type="number"
                        name="purchasePrice"
                        value={formData.purchasePrice}
                        onChange={handleFormChange}
                        required
                        min="0"
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)',
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Current Value *
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRight: 'none',
                          backgroundColor: '#f9fafb',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)',
                        }}
                      >
                        $
                      </span>
                      <input
                        type="number"
                        name="currentValue"
                        value={formData.currentValue}
                        onChange={handleFormChange}
                        required
                        min="0"
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)',
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Depreciation Method *
                    </label>
                    <select
                      name="depreciationMethod"
                      value={formData.depreciationMethod}
                      onChange={handleFormChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      <option value="Straight Line">Straight Line</option>
                      <option value="Declining Balance">
                        Declining Balance
                      </option>
                      <option value="Sum of Years">Sum of Years</option>
                      <option value="Units of Production">
                        Units of Production
                      </option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Useful Life (Years) *
                    </label>
                    <input
                      type="number"
                      name="usefulLife"
                      value={formData.usefulLife}
                      onChange={handleFormChange}
                      required
                      min="1"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Purchase Date *
                    </label>
                    <input
                      type="date"
                      name="purchaseDate"
                      value={formData.purchaseDate}
                      onChange={handleFormChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Status *
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleFormChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Disposed">Disposed</option>
                      <option value="Under Maintenance">
                        Under Maintenance
                      </option>
                      <option value="Retired">Retired</option>
                    </select>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleFormChange}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                        resize: 'vertical',
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    paddingTop: '24px',
                    borderTop: '1px solid #e5e7eb',
                    marginTop: '24px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setSelectedAsset(null);
                    }}
                    className="btn btn-outline"
                    style={{
                      borderRadius: '0',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '0',
                      opacity: isLoading ? 0.6 : 1,
                    }}
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {showAddModal ? 'Create' : 'Update'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedAsset && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease-in-out',
            }}
          >
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '100%',
                maxWidth: '700px',
                height: '100vh',
                backgroundColor: 'white',
                boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideInFromRight 0.3s ease-out',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                }}
              >
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'white',
                    margin: 0,
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  View Financial Valuation
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedAsset(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'white',
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '24px',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px',
                    marginBottom: '24px',
                  }}
                >
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Asset</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      {selectedAsset.assetName} {(typeof selectedAsset.assetId === 'object' && selectedAsset.assetId !== null) ? `(${selectedAsset.assetId.assetCode || selectedAsset.assetId.assetId || selectedAsset.assetId.id || selectedAsset.assetId._id})` : `(${selectedAsset.assetId})`}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Category</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      {selectedAsset.category}{selectedAsset.subcategory ? ` / ${selectedAsset.subcategory}` : ''}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Purchase Price</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      ${(selectedAsset.purchasePrice || 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Current Value</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      ${(selectedAsset.currentValue || 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Depreciation Method</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      {selectedAsset.depreciationMethod || '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Useful Life (Years)</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      {selectedAsset.usefulLife || '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Purchase Date</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      {selectedAsset.purchaseDate ? new Date(selectedAsset.purchaseDate).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Status</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor: selectedAsset.status === 'Active' ? '#d4edda' : '#f8d7da',
                          color: selectedAsset.status === 'Active' ? '#155724' : '#721c24',
                          fontWeight: '500'
                        }}
                      >
                        {selectedAsset.status || '-'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Accumulated Depreciation</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      ${(selectedAsset.accumulatedDepreciation || 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Salvage Value</label>
                    <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px' }}>
                      ${(selectedAsset.salvageValue || 0).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1', marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Notes</label>
                  <div style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', backgroundColor: '#f9fafb', fontSize: '14px', minHeight: '80px', whiteSpace: 'pre-wrap' }}>
                    {selectedAsset.notes || '-'}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: '24px',
                    borderTop: '1px solid #e5e7eb',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedAsset(null);
                    }}
                    className="btn btn-outline"
                    style={{ borderRadius: '0', backgroundColor: '#fff', border: '1px solid #d1d5db', padding: '8px 16px' }}
                  >
                    Close View
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Depreciation Calculation Modal */}
        {showDepreciationModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease-in-out',
            }}
          >
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '100%',
                maxWidth: '650px',
                height: '100vh',
                backgroundColor: 'white',
                boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideInFromRight 0.3s ease-out',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                }}
              >
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'white',
                    margin: 0,
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Depreciation Calculation
                </h2>
                <button
                  type="button"
                  onClick={() => setShowDepreciationModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'white',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '24px',
                }}
              >
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Asset *</label>
                  <select
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    value={depreciationFormData.selectedAssetId || ''}
                    onChange={(e) => {
                      const assetId = e.target.value;
                      const selectedAsset = financialData.find(
                        (asset) =>
                          (asset.assetId || asset._id) === assetId,
                      );
                      setDepreciationFormData((prev) => ({
                        ...prev,
                        selectedAssetId: assetId,
                        cost: selectedAsset
                          ? selectedAsset.purchasePrice
                          : prev.cost,
                      }));
                    }}
                  >
                    <option value="">Select an asset...</option>
                    {financialData.map((asset) => (
                      <option
                        key={asset.assetId || asset._id}
                        value={asset.assetId || asset._id}
                      >
                        {asset.assetName} - $
                        {asset.purchasePrice?.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px',
                    marginBottom: '24px',
                  }}
                >
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Method *</label>
                    <select
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      value={depreciationFormData.method}
                      onChange={(e) =>
                        setDepreciationFormData((prev) => ({
                          ...prev,
                          method: e.target.value,
                        }))
                      }
                    >
                      <option value="Straight Line">Straight Line</option>
                      <option value="Declining Balance">Declining Balance</option>
                      <option value="Sum of Years">Sum of Years</option>
                      <option value="Units of Production">Units of Production</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Cost / Purchase Price *</label>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <span style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRight: 'none', color: '#4b5563', borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px', display: 'flex', alignItems: 'center', fontSize: '14px' }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderTopRightRadius: '4px',
                          borderBottomRightRadius: '4px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        value={depreciationFormData.cost}
                        onChange={(e) =>
                          setDepreciationFormData((prev) => ({
                            ...prev,
                            cost: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Useful Life (Years) *</label>
                    <input
                      type="number"
                      min="1"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      value={depreciationFormData.usefulLife}
                      onChange={(e) =>
                        setDepreciationFormData((prev) => ({
                          ...prev,
                          usefulLife: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Salvage Value</label>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <span style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRight: 'none', color: '#4b5563', borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px', display: 'flex', alignItems: 'center', fontSize: '14px' }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderTopRightRadius: '4px',
                          borderBottomRightRadius: '4px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        value={depreciationFormData.salvageValue}
                        onChange={(e) =>
                          setDepreciationFormData((prev) => ({
                            ...prev,
                            salvageValue: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Depreciation Rate (%)</label>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderTopLeftRadius: '4px',
                          borderBottomLeftRadius: '4px',
                          borderRight: 'none',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        value={depreciationFormData.rate}
                        onChange={(e) =>
                          setDepreciationFormData((prev) => ({
                            ...prev,
                            rate: e.target.value,
                          }))
                        }
                      />
                      <span style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderLeft: 'none', color: '#4b5563', borderTopRightRadius: '4px', borderBottomRightRadius: '4px', display: 'flex', alignItems: 'center', fontSize: '14px' }}>%</span>
                    </div>
                  </div>
                </div>

                {depreciationSchedule.length > 0 && (
                  <div style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                    <h6 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Depreciation Schedule</h6>
                    <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <tr>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#4b5563' }}>Year</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#4b5563' }}>Annual Depreciation</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#4b5563' }}>Accumulated</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#4b5563' }}>Book Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {depreciationSchedule.map((item, index) => (
                            <tr key={index} style={{ borderBottom: index !== depreciationSchedule.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>{item.year}</td>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>${item.annualDepreciation?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td style={{ padding: '12px 16px', color: '#374151' }}>${item.accumulatedDepreciation?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td style={{ padding: '12px 16px', fontWeight: '500', color: '#111827' }}>${item.bookValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  padding: '20px 24px',
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowDepreciationModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDepreciationCalculation}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Calculator size={16} />
                  Calculate Schedule
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Impairment Test Modal */}
        {showImpairmentModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease-in-out',
            }}
          >
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '100%',
                maxWidth: '600px',
                height: '100vh',
                backgroundColor: 'white',
                boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideInFromRight 0.3s ease-out',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                }}
              >
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'white',
                    margin: 0,
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Impairment Test
                </h2>
                <button
                  type="button"
                  onClick={() => setShowImpairmentModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'white',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '24px',
                }}
              >
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Asset *</label>
                  <select
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    value={selectedAsset?.assetId || selectedAsset?._id || ''}
                    onChange={(e) => {
                      const assetId = e.target.value;
                      const asset = financialData.find(
                        (a) => (a.assetId || a._id) === assetId,
                      );
                      setSelectedAsset(asset);
                      if (asset) {
                        setImpairmentFormData((prev) => ({
                          ...prev,
                          carryingAmount:
                            asset.bookValue || asset.currentValue || 0,
                        }));
                      }
                    }}
                    required
                  >
                    <option value="">Select an asset...</option>
                    {financialData.map((asset) => (
                      <option
                        key={asset.assetId || asset._id}
                        value={asset.assetId || asset._id}
                      >
                        {asset.assetName} - Book Value: $
                        {(
                          asset.bookValue ||
                          asset.currentValue ||
                          0
                        ).toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px',
                    marginBottom: '24px',
                  }}
                >
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Test Date *</label>
                    <input
                      type="date"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      value={impairmentFormData.testDate}
                      onChange={(e) =>
                        setImpairmentFormData((prev) => ({
                          ...prev,
                          testDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Test Reason</label>
                    <input
                      type="text"
                      placeholder="Reason for test"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      value={impairmentFormData.testReason}
                      onChange={(e) =>
                        setImpairmentFormData((prev) => ({
                          ...prev,
                          testReason: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Carrying Amount *</label>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <span style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRight: 'none', color: '#4b5563', borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px', display: 'flex', alignItems: 'center', fontSize: '14px' }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderTopRightRadius: '4px',
                          borderBottomRightRadius: '4px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        value={impairmentFormData.carryingAmount}
                        onChange={(e) =>
                          setImpairmentFormData((prev) => ({
                            ...prev,
                            carryingAmount: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Recoverable Amount *</label>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <span style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRight: 'none', color: '#4b5563', borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px', display: 'flex', alignItems: 'center', fontSize: '14px' }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderTopRightRadius: '4px',
                          borderBottomRightRadius: '4px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        value={impairmentFormData.recoverableAmount}
                        onChange={(e) =>
                          setImpairmentFormData((prev) => ({
                            ...prev,
                            recoverableAmount: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Notes</label>
                  <textarea
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      resize: 'vertical'
                    }}
                    value={impairmentFormData.notes}
                    onChange={(e) =>
                      setImpairmentFormData((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  padding: '20px 24px',
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowImpairmentModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImpairmentTest}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#eab308',
                    border: 'none',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <AlertTriangle size={16} />
                  Perform Test
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showImpairmentDeleteModal}
          onClose={() => {
            setShowImpairmentDeleteModal(false);
            setImpairmentToDelete(null);
          }}
          onConfirm={handleConfirmDeleteImpairmentTest}
          isProcessing={isLoading}
          title="Delete Impairment Test"
          message="Are you sure you want to delete this Impairment Test? This action cannot be undone and will restore the original valuation figures."
          confirmText="Delete Test"
          confirmType="danger"
          itemType="Impairment Test"
          itemName={impairmentToDelete ? `${impairmentToDelete.assetName} (${new Date(impairmentToDelete.testDate).toLocaleDateString()})` : 'this record'}
        />

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </div>
  );
};

export default FinancialValuation;
